import { memo, useCallback, useRef } from 'react'
import Link from 'next/link'
//import CompareToggle from '../compare-toggle'
import { NotificationType, VehicleStatus } from '../../api-client/enums'
import truncateString from '../../utils/truncate-string'
import useNotifications from '../../hooks/use-notifications'
import FavoriteToggle from '../favorite-toggle'
import Image from '../image'
import { now } from '../../utils/date-service'
import { MAX_AGE_OF_VEHICLE_FOR_LEASE } from '../../constants'

const ListItem = memo(
  ({
    id,
    imageFileKey,
    name,
    trim,
    mileage,
    price,
    leaseAmount,
    financeAmount,
    // compareIndex,
    isFavorite,
    onFavorite,
    isFavoriteProcessing,
    href,
    status,
    ribbon,
    year,
  }) => {
    const linkRef = useRef()
    const { markNotificationAsRead } = useNotifications(NotificationType.Favorite)

    const handleFavoriteClick = useCallback(
      (e) => {
        onFavorite(id, isFavorite)
        markNotificationAsRead(id)
        e.stopPropagation()
      },
      [id, isFavorite, onFavorite, markNotificationAsRead],
    )

    const handleVehicleImageClick = () => linkRef.current.click()

    const minSupportedLeaseYear = now().getFullYear() - MAX_AGE_OF_VEHICLE_FOR_LEASE

    return (
      <div className='carListItem' onClick={handleVehicleImageClick}>
        <div className='container flex alignCenter'>
          <div className='image cell'>
            <Image src={imageFileKey} alt={name} width='150' height='100' />
          </div>

          <div className='name cell'>
            <Link legacyBehavior href={href}>
              <a ref={linkRef}>{truncateString(name)}</a>
            </Link>
          </div>

          <div className='cell'>
            <span className='trim'>{truncateString(trim)}</span>
          </div>

          <div className='mileage cell'>{mileage.toLocaleString()} ml</div>

          <div className='finance cell'>
            <span className='label'>Finance</span>
            <span className='value'>
              <b>{financeAmount ? `$${financeAmount.toLocaleString()}/mo` : 'N/A'}</b>
            </span>
          </div>

          <div className='lease cell'>
            {year >= minSupportedLeaseYear && (
              <>
                <span className='label'>Lease</span>
                <span className='value'>
                  <b>{leaseAmount ? `$${leaseAmount.toLocaleString()}/mo` : 'N/A'}</b>
                </span>
              </>
            )}
          </div>

          <div className='price cell'>${price.toLocaleString()}</div>

          <div className='auctionDueDateBadge cell'>
            {ribbon && <div className='ribbon flex alignCenter justifyCenter'>{ribbon}</div>}
          </div>

          {/* TODO: connect to the flow once the compare module is ready */}
          {/* <div className='compareToggleContainer cell'>
            <CompareToggle
              index={compareIndex}
              inputProps={{ 'aria-label': 'compare', name: 'temporary' }}
            />
          </div> */}

          <div className='favoriteContainer cell'>
            <FavoriteToggle
              size='sm'
              onClick={handleFavoriteClick}
              isFavorite={isFavorite}
              isFavoriteProcessing={isFavoriteProcessing}
            />
          </div>
        </div>

        <style jsx>{`
          .carListItem {
            position: relative;
            overflow: hidden;
            box-shadow: 0 0.3rem 0.6rem rgba(0, 0, 0, 0.16);
            border-radius: 1rem;
            background: #fff;
            z-index: 1;
            font-size: 1.8rem;
            cursor: pointer;

            .container {
              width: 100%;
              height: 7.7rem;
            }

            .cell {
              flex: 1;

              &:not(.image) {
                padding: 0 1rem;
              }

              &.name {
                padding-left: 4rem;
                font-weight: 700;
              }

              &.image {
                max-width: 13.7rem;
                min-width: 7.7rem;
                height: 100%;
                cursor: pointer;

                :global(img) {
                  width: 100%;
                  height: 100%;
                  object-fit: cover;
                }
              }

              &.mileage {
                color: var(--secondary-text-color);
              }

              &.lease,
              &.finance {
                .label {
                  margin: 0 0.7rem 0 0;
                  color: var(--secondary-text-color);
                }
              }

              &.price {
                padding-left: 4rem;
                color: var(--primary-color);
                font-size: 3.2rem;
              }

              &.auctionDueDateBadge {
                max-width: 15rem;

                .ribbon {
                  height: 3.8rem;
                  width: 100%;
                  padding: 0 1rem;
                  border-radius: 2rem;
                }
              }

              &.compareToggleContainer {
                max-width: 16rem;
              }

              &.favoriteContainer {
                position: relative;
                top: 0.3rem;
                max-width: 7rem;

                :global(svg) {
                  width: 3.4rem;
                  height: 3.2rem;

                  :global(path) {
                    stroke-width: 4px;
                  }
                }
              }
            }

            .trim {
              color: var(--secondary-text-color);
              font-size: 1.6rem;
            }
          }
        `}</style>

        <style jsx>{`
          .ribbon {
            background: ${status === VehicleStatus.ApprovedForBuyNow
              ? 'var(--primary-color)'
              : 'var(--primary-text-color)'};
          }
        `}</style>
      </div>
    )
  },
)

export default ListItem
